import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Alert,
  CircularProgress,
  Chip,
  Tabs,
  Tab,
  Card,
  CardContent,
  Divider,
  Stack,
  InputAdornment,
  IconButton,
  Tooltip,
  Pagination,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import api, { clearanceAPI, feeAPI } from '../../services/api';

const ClearanceCardGeneration = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [terms, setTerms] = useState([]);
  const [settings, setSettings] = useState([]);
  const [classStreams, setClassStreams] = useState([]);
  const [selectedTerm, setSelectedTerm] = useState('');
  const [selectedSetting, setSelectedSetting] = useState('');
  const [selectedClassStream, setSelectedClassStream] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [genderFilter, setGenderFilter] = useState('');
  const [perPage, setPerPage] = useState(15);
  const [currentPage, setCurrentPage] = useState(0);
  const [pagination, setPagination] = useState(null);
  const [eligibleStudents, setEligibleStudents] = useState([]);
  const [ineligibleStudents, setIneligibleStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [clearedStudents, setClearedStudents] = useState([]);
  const [clearedPagination, setClearedPagination] = useState(null);
  const [clearedPage, setClearedPage] = useState(0);
  const [clearedPerPage, setClearedPerPage] = useState(15);
  const [clearedSearch, setClearedSearch] = useState('');
  const [clearedGender, setClearedGender] = useState('');
  const [loadingCleared, setLoadingCleared] = useState(false);
  const [eligibilityData, setEligibilityData] = useState(null);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (activeTab === 1) {
      fetchClearedStudents();
    }
  }, [activeTab, selectedTerm, selectedSetting, selectedClassStream, clearedPage, clearedPerPage, clearedSearch, clearedGender]);

  useEffect(() => {
    if (selectedTerm && selectedSetting) {
      handleCheckEligibility();
    }
  }, [selectedTerm, selectedSetting, currentPage, perPage, selectedClassStream, searchQuery, genderFilter]);

  const fetchInitialData = async () => {
    try {
      console.log('Fetching initial data...');
      const [termsRes, settingsRes, classStreamsRes] = await Promise.all([
        feeAPI.getTerms(),
        clearanceAPI.getSettings(),
        feeAPI.getClassStreams(),
      ]);
      console.log('Terms:', termsRes.data);
      console.log('Settings:', settingsRes.data);
      console.log('Class Streams:', classStreamsRes.data);
      setTerms(termsRes.data);
      setSettings(settingsRes.data);
      setClassStreams(classStreamsRes.data);
      
      // Auto-select current term (first in list)
      if (termsRes.data.length > 0) {
        setSelectedTerm(termsRes.data[0].id);
      }
      
      // Auto-select the active setting (should be only one)
      if (settingsRes.data.length > 0) {
        setSelectedSetting(settingsRes.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setMessage({ type: 'error', text: 'Failed to load initial data: ' + error.message });
    }
  };

  const handleCheckEligibility = async () => {
    if (!selectedTerm || !selectedSetting) {
      setMessage({ type: 'error', text: 'Please select term and clearance setting' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });
    setEligibleStudents([]);
    setIneligibleStudents([]);
    setSelectedStudents([]);
    setEligibilityData(null);

    try {
      const payload = {
        term_id: selectedTerm,
        clearance_setting_id: selectedSetting,
        page: currentPage + 1,
        per_page: perPage,
      };
      
      if (selectedClassStream) payload.class_stream_id = selectedClassStream;
      if (searchQuery) payload.search = searchQuery;
      if (genderFilter) payload.gender = genderFilter;

      const response = await clearanceAPI.checkEligibility(payload);

      setEligibleStudents(response.data.eligible_students);
      setIneligibleStudents(response.data.ineligible_students);
      setEligibilityData(response.data);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Eligibility check error:', error);
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to check eligibility' });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedStudents(eligibleStudents.map((s) => s.id));
    } else {
      setSelectedStudents([]);
    }
  };

  const handleSelectStudent = (studentId) => {
    setSelectedStudents((prev) =>
      prev.includes(studentId) ? prev.filter((id) => id !== studentId) : [...prev, studentId]
    );
  };

  const handleGenerateCards = async () => {
    if (selectedStudents.length === 0) {
      setMessage({ type: 'error', text: 'Please select at least one student' });
      return;
    }

    setGenerating(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await clearanceAPI.generateCards({
        term_id: selectedTerm,
        clearance_setting_id: selectedSetting,
        student_ids: selectedStudents,
      });

      setMessage({ type: 'success', text: response.data.message });
      handleCheckEligibility();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to generate cards' });
    } finally {
      setGenerating(false);
    }
  };

  const handlePrintCards = async () => {
    if (selectedStudents.length === 0) {
      setMessage({ type: 'error', text: 'Please select at least one student' });
      return;
    }

    try {
      const response = await clearanceAPI.downloadCards({
        term_id: selectedTerm,
        clearance_setting_id: selectedSetting,
        student_ids: selectedStudents,
      });

      const { school, cards } = response.data;
      
      // Open print window
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        setMessage({ type: 'error', text: 'Please allow popups to print cards' });
        return;
      }

      // Generate HTML for print
      const cardsHtml = cards.map(card => `
        <div style="
          width: 85.6mm;
          height: 53.98mm;
          border: 3px solid #1b5e20;
          border-radius: 12px;
          background: #fff;
          overflow: hidden;
          position: relative;
          page-break-after: always;
          page-break-inside: avoid;
        ">
          <div style="
            background: linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%);
            padding: 6px 10px;
            text-align: center;
            position: relative;
          ">
            ${school.logo ? `<img src="${school.logo}" style="position: absolute; left: 8px; top: 50%; transform: translateY(-50%); height: 20px; width: auto;" />` : ''}
            <div style="font-size: 10px; font-weight: 800; color: #fff; text-transform: uppercase; letter-spacing: 0.5px;">
              ${school.name}
            </div>
            <div style="font-size: 7px; font-weight: 600; color: #e8f5e9; margin-top: 1px;">
              CLEARANCE CARD
            </div>
          </div>
          <div style="padding: 10px 10px 12px; display: flex; gap: 10px;">
            <div style="
              width: 60px;
              height: 70px;
              border: 2px solid #2e7d32;
              border-radius: 50%;
              overflow: hidden;
              flex-shrink: 0;
              background: #f1f8f4;
            ">
              <img src="${card.student.profile_picture}" style="width: 100%; height: 100%; object-fit: cover;" />
            </div>
            <div style="flex: 1;">
              <div style="margin-bottom: 6px;">
                <div style="font-size: 6px; color: #666; font-weight: 700; letter-spacing: 0.3px; margin-bottom: 1px;">STUDENT NAME</div>
                <div style="font-size: 10px; font-weight: 800; color: #1b5e20; line-height: 1.2;">
                  ${card.student.last_name} ${card.student.first_name}
                </div>
              </div>
              <div style="display: flex; gap: 8px;">
                <div style="flex: 1;">
                  <div style="margin-bottom: 6px;">
                    <div style="font-size: 6px; color: #666; font-weight: 700; letter-spacing: 0.3px; margin-bottom: 1px;">REGISTRATION NO</div>
                    <div style="font-size: 9px; font-weight: 700; color: #333; font-family: monospace;">
                      ${card.student.registration_no}
                    </div>
                  </div>
                  <div style="margin-bottom: 6px;">
                    <div style="font-size: 6px; color: #666; font-weight: 700; letter-spacing: 0.3px; margin-bottom: 1px;">GENDER</div>
                    <div style="font-size: 9px; font-weight: 700; color: #333; text-transform: uppercase;">
                      ${card.student.gender}
                    </div>
                  </div>
                </div>
                <div style="flex: 1;">
                  <div style="margin-bottom: 6px;">
                    <div style="font-size: 6px; color: #666; font-weight: 700; letter-spacing: 0.3px; margin-bottom: 1px;">CLASS</div>
                    <div style="font-size: 9px; font-weight: 700; color: #333;">
                      ${card.student.class_stream_name}
                    </div>
                  </div>
                  <div style="margin-bottom: 6px;">
                    <div style="font-size: 6px; color: #666; font-weight: 700; letter-spacing: 0.3px; margin-bottom: 1px;">TERM</div>
                    <div style="font-size: 9px; font-weight: 700; color: #333;">
                      ${card.term_name} ${card.term_year}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div style="
            margin: 0 10px 2px;
            padding-top: 1px;
            border-top: 2px solid #e8f5e9;
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
          ">
            <div style="text-align: center;">
              <div style="font-size: 5px; color: #666; margin-bottom: 2px;">HEAD TEACHER</div>
              <div style="border-top: 1px solid #333; width: 45px; margin: 0 auto;"></div>
            </div>
            <div style="text-align: center;">
              <div style="font-size: 5px; color: #666; margin-bottom: 2px;">BURSAR</div>
              <div style="border-top: 1px solid #333; width: 45px; margin: 0 auto;"></div>
            </div>
            <div style="
              display: flex;
              align-items: center;
              gap: 2px;
              background: #e8f5e9;
              padding: 2px 5px;
              border-radius: 3px;
            ">
              <svg width="9" height="9" viewBox="0 0 24 24" fill="#2e7d32">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
              <div>
                <div style="font-size: 5px; color: #1b5e20; font-weight: 700;">CLEARED</div>
                <div style="font-size: 6px; font-weight: 800; color: #2e7d32;">
                  ${new Date(card.generated_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                </div>
              </div>
            </div>
          </div>
        </div>
      `).join('');

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Clearance Cards - ${school.name}</title>
          <style>
            @page { size: A4; margin: 5mm; }
            * { margin: 0; padding: 0; box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            body { font-family: Arial, sans-serif; }
            .cards-container {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 10mm;
              padding: 10mm;
            }
            @media print {
              .no-print { display: none !important; }
              .cards-container { gap: 5mm; padding: 5mm; }
            }
          </style>
        </head>
        <body>
          <div class="no-print" style="position: fixed; top: 20px; right: 20px; z-index: 1000;">
            <button onclick="window.print()" style="
              padding: 10px 20px;
              background: #2e7d32;
              color: white;
              border: none;
              border-radius: 4px;
              cursor: pointer;
              font-size: 14px;
            ">Print Cards</button>
            <button onclick="window.close()" style="
              padding: 10px 20px;
              background: #666;
              color: white;
              border: none;
              border-radius: 4px;
              cursor: pointer;
              font-size: 14px;
              margin-left: 10px;
            ">Close</button>
          </div>
          <div class="cards-container">
            ${cardsHtml}
          </div>
        </body>
        </html>
      `);
      printWindow.document.close();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to load cards for printing' });
    }
  };

  const fetchClearedStudents = async () => {
    setLoadingCleared(true);
    try {
      const params = {
        page: clearedPage + 1,
        per_page: clearedPerPage,
      };
      if (selectedTerm) params.term_id = selectedTerm;
      if (selectedSetting) params.clearance_setting_id = selectedSetting;
      if (selectedClassStream) params.class_stream_id = selectedClassStream;
      if (clearedSearch) params.search = clearedSearch;
      if (clearedGender) params.gender = clearedGender;

      console.log('Fetching cleared students with params:', params);
      const response = await clearanceAPI.getClearedStudents(params);
      console.log('Cleared students response:', response.data);
      setClearedStudents(response.data.data || []);
      setClearedPagination({
        total: response.data.total,
        current_page: response.data.current_page,
        last_page: response.data.last_page,
      });
    } catch (error) {
      console.error('Error fetching cleared students:', error);
    } finally {
      setLoadingCleared(false);
    }
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setGenderFilter('');
    setSelectedClassStream('');
  };

  const handleClearedClearFilters = () => {
    setClearedSearch('');
    setClearedGender('');
    setSelectedClassStream('');
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight="bold">
            Student Clearance Cards
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Generate and manage student clearance cards based on fee payment status
          </Typography>
        </Box>
      </Box>

      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 3 }}>
        <Tab label="Generate Cards" />
        <Tab label="Cleared Students" />
      </Tabs>

      {message.text && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage({ type: '', text: '' })}>
          {message.text}
        </Alert>
      )}

      {activeTab === 0 && (
        <>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Select Criteria
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  select
                  SelectProps={{ native: true }}
                  value={selectedTerm}
                  onChange={(e) => setSelectedTerm(e.target.value)}
                  size="small"
                >
                  <option value="">Select Term</option>
                  {terms.map((term) => (
                    <option key={term.id} value={term.id}>
                      {term.name} {term.year}
                    </option>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  select
                  SelectProps={{ native: true }}
                  value={selectedSetting}
                  onChange={(e) => setSelectedSetting(e.target.value)}
                  size="small"
                >
                  <option value="">Select Clearance Type</option>
                  {settings.map((setting) => (
                    <option key={setting.id} value={setting.id}>
                      {setting.name} ({setting.percentage}%)
                    </option>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  select
                  SelectProps={{ native: true }}
                  value={selectedClassStream}
                  onChange={(e) => setSelectedClassStream(e.target.value)}
                  size="small"
                >
                  <option value="">All Classes</option>
                  {classStreams.map((cs) => (
                    <option key={cs.id} value={cs.id}>
                      {cs.name}
                    </option>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  select
                  SelectProps={{ native: true }}
                  value={genderFilter}
                  onChange={(e) => setGenderFilter(e.target.value)}
                  size="small"
                >
                  <option value="">All Genders</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </TextField>
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  placeholder="Search by name, registration, or LIN"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearchQuery('')}>
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  fullWidth
                  variant="outlined"
                  size="large"
                  startIcon={<ClearIcon />}
                  onClick={handleClearFilters}
                >
                  Clear Filters
                </Button>
              </Grid>
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                  onClick={() => {
                    setCurrentPage(0);
                    handleCheckEligibility();
                  }}
                  disabled={loading || !selectedTerm || !selectedSetting}
                >
                  {loading ? 'Checking Eligibility...' : 'Check Eligibility'}
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {eligibleStudents.length > 0 && (
            <Paper sx={{ mb: 3 }}>
              <Box p={2} display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" fontWeight="bold" display="flex" alignItems="center">
                  <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                  Eligible Students
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircleIcon />}
                    onClick={handleGenerateCards}
                    disabled={generating || selectedStudents.length === 0}
                    size="small"
                  >
                    {generating ? 'Generating...' : `Generate (${selectedStudents.length})`}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<PrintIcon />}
                    onClick={handlePrintCards}
                    disabled={selectedStudents.length === 0}
                    size="small"
                  >
                    Print Cards
                  </Button>
                </Stack>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={selectedStudents.length === eligibleStudents.length && eligibleStudents.length > 0}
                          indeterminate={
                            selectedStudents.length > 0 && selectedStudents.length < eligibleStudents.length
                          }
                          onChange={(e) => handleSelectAll(e.target.checked)}
                        />
                      </TableCell>
                      <TableCell>Reg No</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Class</TableCell>
                      <TableCell align="right">Total Fees</TableCell>
                      <TableCell align="right">Paid</TableCell>
                      <TableCell align="right">Payment %</TableCell>
                      <TableCell align="center">Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {eligibleStudents.map((student) => (
                      <TableRow key={student.id} hover>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={selectedStudents.includes(student.id)}
                            onChange={() => handleSelectStudent(student.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="primary" fontWeight="medium">
                            {student.registration_no}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {student.full_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={student.class_stream_name} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">{student.total_fees.toLocaleString()}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">{student.paid_amount.toLocaleString()}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={`${student.percentage_paid.toFixed(1)}%`}
                            color="success"
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip label="Eligible" color="success" size="small" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {pagination && pagination.total > perPage && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'stretch', sm: 'center' },
                    gap: 2,
                    p: 2,
                    borderTop: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Showing{" "}
                      {pagination.total === 0 ? 0 : (currentPage * perPage) + 1} to{" "}
                      {Math.min((currentPage + 1) * perPage, pagination.total)}{" "}
                      of {pagination.total} entries
                    </Typography>
                    
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        Rows per page:
                      </Typography>
                      <FormControl size="small">
                        <Select
                          value={perPage}
                          onChange={(e) => {
                            setPerPage(parseInt(e.target.value, 10));
                            setCurrentPage(0);
                          }}
                          sx={{ minWidth: 70 }}
                        >
                          <MenuItem value={10}>10</MenuItem>
                          <MenuItem value={15}>15</MenuItem>
                          <MenuItem value={20}>20</MenuItem>
                          <MenuItem value={50}>50</MenuItem>
                          <MenuItem value={100}>100</MenuItem>
                        </Select>
                      </FormControl>
                    </Stack>
                  </Stack>

                  <Pagination
                    count={pagination.last_page}
                    page={currentPage + 1}
                    onChange={(e, page) => setCurrentPage(page - 1)}
                    color="primary"
                    size="small"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </Paper>
          )}

          {ineligibleStudents.length > 0 && (
            <Paper>
              <Box p={2}>
                <Typography variant="h6" fontWeight="bold" display="flex" alignItems="center">
                  <CancelIcon color="error" sx={{ mr: 1 }} />
                  Ineligible Students ({ineligibleStudents.length})
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={0.5}>
                  These students have not met the minimum payment requirement
                </Typography>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Reg No</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Class</TableCell>
                      <TableCell align="right">Total Fees</TableCell>
                      <TableCell align="right">Paid</TableCell>
                      <TableCell align="right">Payment %</TableCell>
                      <TableCell align="center">Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ineligibleStudents.map((student) => (
                      <TableRow key={student.id} hover>
                        <TableCell>{student.registration_no}</TableCell>
                        <TableCell>{student.full_name}</TableCell>
                        <TableCell><Chip label={student.class_stream_name} size="small" variant="outlined" /></TableCell>
                        <TableCell align="right">{student.total_fees.toLocaleString()}</TableCell>
                        <TableCell align="right">{student.paid_amount.toLocaleString()}</TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={`${student.percentage_paid.toFixed(1)}%`}
                            color="error"
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip label="Not Eligible" color="error" size="small" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      )}

      {activeTab === 1 && (
        <>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Search students"
                  value={clearedSearch}
                  onChange={(e) => setClearedSearch(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: clearedSearch && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setClearedSearch('')}>
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  select
                  label="Gender"
                  SelectProps={{ native: true }}
                  value={clearedGender}
                  onChange={(e) => setClearedGender(e.target.value)}
                  size="small"
                >
                  <option value="">All</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </TextField>
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={handleClearedClearFilters}
                  size="large"
                >
                  Clear
                </Button>
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  fullWidth
                  startIcon={<RefreshIcon />}
                  onClick={fetchClearedStudents}
                  variant="contained"
                  size="large"
                >
                  Refresh
                </Button>
              </Grid>
            </Grid>
          </Paper>
          <Paper>
            <Box p={2} display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" fontWeight="bold">
                Cleared Students History
              </Typography>
              {clearedPagination && (
                <Typography variant="body2" color="text.secondary">
                  Total: {clearedPagination.total} students
                </Typography>
              )}
            </Box>
          {loadingCleared ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Reg No</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Class</TableCell>
                      <TableCell>Term</TableCell>
                      <TableCell>Clearance Type</TableCell>
                      <TableCell align="right">Paid %</TableCell>
                      <TableCell>Generated At</TableCell>
                      <TableCell>Generated By</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {clearedStudents.map((card) => (
                      <TableRow key={card.id}>
                        <TableCell>{card.student.registration_no}</TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">{card.student.full_name}</Typography>
                            <Typography variant="caption" color="text.secondary">{card.student.gender}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{card.student.class_stream_name}</TableCell>
                        <TableCell>{card.term.name}</TableCell>
                        <TableCell>
                          <Chip label={card.clearance_setting.name} size="small" />
                        </TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={`${parseFloat(card.percentage_paid).toFixed(1)}%`}
                            color="success"
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{new Date(card.generated_at).toLocaleString()}</TableCell>
                        <TableCell>{card.generated_by}</TableCell>
                      </TableRow>
                    ))}
                    {clearedStudents.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          <Typography variant="body2" color="text.secondary" py={3}>
                            No cleared students found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {clearedPagination && clearedPagination.total > clearedPerPage && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'stretch', sm: 'center' },
                    gap: 2,
                    p: 2,
                    borderTop: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Showing{" "}
                      {clearedPagination.total === 0 ? 0 : (clearedPage * clearedPerPage) + 1} to{" "}
                      {Math.min((clearedPage + 1) * clearedPerPage, clearedPagination.total)}{" "}
                      of {clearedPagination.total} entries
                    </Typography>
                    
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        Rows per page:
                      </Typography>
                      <FormControl size="small">
                        <Select
                          value={clearedPerPage}
                          onChange={(e) => {
                            setClearedPerPage(parseInt(e.target.value, 10));
                            setClearedPage(0);
                          }}
                          sx={{ minWidth: 70 }}
                        >
                          <MenuItem value={10}>10</MenuItem>
                          <MenuItem value={15}>15</MenuItem>
                          <MenuItem value={20}>20</MenuItem>
                          <MenuItem value={50}>50</MenuItem>
                          <MenuItem value={100}>100</MenuItem>
                        </Select>
                      </FormControl>
                    </Stack>
                  </Stack>

                  <Pagination
                    count={clearedPagination.last_page}
                    page={clearedPage + 1}
                    onChange={(e, page) => setClearedPage(page - 1)}
                    color="primary"
                    size="small"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </>
          )}
          </Paper>
        </>
      )}
    </Box>
  );
};

export default ClearanceCardGeneration;
